import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useAuthStore } from './store'
import type { User } from './types'

/**
 * The account menu (top-right avatar) is the same everywhere in Carousel: the
 * app, the marketing pages, org pages and the org admin. Profile, My stats,
 * Your account, Log out. Every host spreads these props into AppBar or
 * UserMenu instead of choosing its own, so the menus cannot drift apart.
 * (Your teams and orgs live in the logo rail and the side nav, not here.)
 */
export interface AccountMenuProps {
  user: { name: string; avatarUrl?: string } | null
  profileUrl: string
  statsUrl?: string
  settingsUrl: string
  settingsLabel: string
}

export const ACCOUNT_SETTINGS_URL = '/settings'
export const ACCOUNT_SETTINGS_LABEL = 'Your account'
/** Where Log out lands, from anywhere: the public home page. */
export const AFTER_LOGOUT = '/'

/** The menu for a signed-in user, or the signed-out one (Log in / Sign up). */
export function accountMenuProps(user: User | null): AccountMenuProps {
  return {
    user: user ? { name: user.gamer_tag || user.username, avatarUrl: user.avatar_url || undefined } : null,
    profileUrl: user ? `/u/${user.username}` : '/login',
    statsUrl: user ? `/u/${user.username}/stats` : undefined,
    settingsUrl: ACCOUNT_SETTINGS_URL,
    settingsLabel: ACCOUNT_SETTINGS_LABEL,
  }
}

/** The account menu's props for the current user, with Log out wired up. */
export function useAccountMenu(): AccountMenuProps & { onLogout: () => void } {
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()

  const onLogout = useCallback(() => {
    logout()
    navigate(AFTER_LOGOUT, { replace: true })
  }, [logout, navigate])

  return { ...accountMenuProps(isAuthenticated ? user : null), onLogout }
}
